import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import { Button, Form } from "react-bootstrap";
import setUnregisteredPatientEmail from "services/patient-chart/setUngisteredPatientEmail";
import addOrEditSessionStyles from "../../AddOrEditSession.module.scss";

interface MissingEmailModalProps {
  error: any;
  setError: React.Dispatch<any>;
  showMissEmailModal: boolean;
  setShowMissEmailModal: React.Dispatch<React.SetStateAction<boolean>>;
  email: string | null;
  client: PatientChartResponseEntry;
  setEmail: React.Dispatch<React.SetStateAction<string | null>>;
  setBillingContact: React.Dispatch<React.SetStateAction<string>>;
  billingContact: string;
  handleApply: () => Promise<void>;
  trackClick: (elementName: string) => void;
  invoicing: boolean;
}

const MissingEmailModal = ({
  error,
  setError,
  showMissEmailModal,
  setShowMissEmailModal,
  email,
  client,
  setEmail,
  setBillingContact,
  billingContact,
  handleApply,
  trackClick,
  invoicing,
}: MissingEmailModalProps) => {
  const { guardians, couple_members, patient } = client;

  const isCouple = !!couple_members && couple_members.length > 0;
  const isMinor = !!guardians && guardians.length > 0;

  const setEmailForNewPatient = async (email) => {
    if (!patient) return;
    try {
      if (isCouple && !!couple_members) {
        await setUnregisteredPatientEmail(
          billingContact === "patient2"
            ? couple_members[1].orchid_user_id
            : couple_members[0].orchid_user_id,
          email,
        );
      } else {
        if (isMinor && !!guardians) {
          // minor/guardian
          await setUnregisteredPatientEmail(
            billingContact === "patient2"
              ? guardians[0].contact.orchid_user_id
              : patient.orchid_user_id,
            email,
          );
        } else {
          // adult
          await setUnregisteredPatientEmail(patient.orchid_user_id, email);
        }
      }
      setShowMissEmailModal(false);
      handleApply();
    } catch (error) {
      setError(error);
    }
  };
  return (
    <ModalLayout
      className={addOrEditSessionStyles["missing-email-modal"]}
      headerClass={addOrEditSessionStyles["missing-email-modal__header"]}
      bodyClass={addOrEditSessionStyles["missing-email-modal__body"]}
      footerClass={addOrEditSessionStyles["missing-email-modal__footer"]}
      contentClassName={addOrEditSessionStyles["missing-email-modal__content"]}
      size={"md"}
      show={showMissEmailModal}
      onHide={() => {
        trackClick("cancel-button");
        setShowMissEmailModal(false);
        setEmail(null);
        setBillingContact("patient1");
      }}
      error={error}
      Header={"Missing Email Address!"}
      Footer={
        <>
          {!invoicing ? (
            <Button
              variant={"outline-primary"}
              onClick={() => {
                trackClick("cancel-button");
                setShowMissEmailModal(false);
                setEmail(null);
                setBillingContact("patient1");
              }}
            >
              Cancel
            </Button>
          ) : (
            <Button
              variant={"outline-primary"}
              onClick={() => {
                trackClick("skip-button");
                setEmailForNewPatient(email);
              }}
            >
              Skip
            </Button>
          )}
          <Button
            disabled={!email}
            onClick={() => {
              trackClick("confirm-button");
              setEmailForNewPatient(email);
            }}
          >
            Confirm Email
          </Button>
        </>
      }
    >
      {isCouple ? (
        <div>
          {invoicing
            ? "Enter an email below if you'd like your patient to receive any communications from Orchid about their session(s). If you skip, the patient will not receive any invites or any reminders from Orchid."
            : "Enter an email address below to send your patient an invite for confirmation. Without an email, the patient cannot confirm the session, and payment cannot be authorized."}
        </div>
      ) : (
        <div>
          {invoicing
            ? "Enter an email below if you'd like your patient to receive any communications from Orchid about their session(s). If you skip, the patient will not receive any invites or any reminders from Orchid."
            : "Enter an email address below to send your patient an invite for confirmation. Without an email, the patient cannot confirm the session, and payment cannot be authorized."}
        </div>
      )}

      <Form.Group className={"mt-4"} controlId="email">
        <Form.Control
          type="text"
          value={email || ""}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={"Enter Email address"}
          onClick={() => trackClick("email-address-field")}
        />
      </Form.Group>
      {isCouple && !!couple_members ? (
        <div
          className={addOrEditSessionStyles["radio-group"]}
          role="radiogroup"
          aria-labelledby={"Billing Contact"}
        >
          {[
            { value: "patient1", label: "Partner 1" },
            { value: "patient2", label: "Partner 2" },
          ].map((el, i) => (
            <div
              key={el.value}
              className={addOrEditSessionStyles["radio-option"]}
            >
              <CustomSelectionControl
                type={"radio"}
                checked={billingContact === el.value}
                onChange={() => {
                  setBillingContact(el.value);
                  trackClick("billing-contact-radio-button");
                }}
              />{" "}
              {couple_members[i].first_name || ""}{" "}
              {couple_members[i].last_name || ""}
            </div>
          ))}
        </div>
      ) : (
        <div
          className={addOrEditSessionStyles["radio-group"]}
          role="radiogroup"
          aria-labelledby={"Billing Contact"}
        >
          <div className={addOrEditSessionStyles["radio-option"]}>
            <CustomSelectionControl
              type={"radio"}
              checked={billingContact === "patient1"}
              onChange={() => {
                setBillingContact("patient1");
                trackClick("billing-contact-radio-button");
              }}
            />{" "}
            Patient
          </div>
          {isMinor && (
            <div className={addOrEditSessionStyles["radio-option"]}>
              <CustomSelectionControl
                type={"radio"}
                checked={billingContact === "patient2"}
                onChange={() => {
                  setBillingContact("patient2");
                  trackClick("billing-contact-radio-button");
                }}
              />{" "}
              Primary Contact
            </div>
          )}
        </div>
      )}
    </ModalLayout>
  );
};

export default MissingEmailModal;
